import React, { ReactElement, useContext, useState } from 'react';
import { Nullable } from '../../../models';
import { logger } from '../../../services';
import {
  CustomersSubscriptionPopupContextModel,
  CustomersSubscriptionPopupProviderProps,
} from './customersSubscriptionPopup.model';
import { CustomersSubscriptionPopup } from './customersSubscriptionPopup.container';
import {
  customersSubscriptionActivateProvider,
  CustomersSubscriptions,
  CustomerSubscriptionStatuses,
} from '../customersSubscription';
import {
  getInitialPopupState,
  getInitialStatusState,
} from './customersSubscriptionPopup.helper';
import { TechnicalIssuePopup } from '../technicalIssuePopup';

const CustomersSubscriptionPopupContext =
  React.createContext<Nullable<CustomersSubscriptionPopupContextModel>>(null);

export const CustomersSubscriptionPopupContextProvider = ({
  children,
  subscriptions,
  handleOpenHelpForm,
}: CustomersSubscriptionPopupProviderProps): ReactElement => {
  const [isPopupOpened, setIsPopupOpened] = useState<boolean>(
    getInitialPopupState(subscriptions)
  );
  const [status, setStatus] = useState<CustomerSubscriptionStatuses>(
    getInitialStatusState(subscriptions)
  );

  const handleOpenPopup = () => {
    setIsPopupOpened(true);
  };

  const handleClosePopup = () => {
    setIsPopupOpened(false);
  };

  const handleActivate = async () => {
    setIsPopupOpened(true);
    setStatus(CustomerSubscriptionStatuses.loading);
    await customersSubscriptionActivateProvider(CustomersSubscriptions.kcell)
      .then(async () => {
        await new Promise((res) => setTimeout(res, 5000));
        setStatus(CustomerSubscriptionStatuses.created);
      })
      .catch((error) => {
        setStatus(
          error?.response?.data?.errors?.[0]?.type ||
            CustomerSubscriptionStatuses.unknown
        );
        console.log('[ERROR]: can not activate kcell subscription', error);
      });
  };

  const context = {
    status,
    handleActivate,
    handleOpenPopup,
    handleClosePopup,
    handleOpenHelpForm,
    isPopupOpened,
  };

  return (
    <CustomersSubscriptionPopupContext.Provider value={context}>
      {children}
      <CustomersSubscriptionPopup />
      <TechnicalIssuePopup />
    </CustomersSubscriptionPopupContext.Provider>
  );
};

export const useCustomersSubscriptionPopupContext =
  (): CustomersSubscriptionPopupContextModel => {
    const value = useContext(CustomersSubscriptionPopupContext);

    if (!value) {
      logger.error(
        '[ERROR]: CustomersSubscriptionPopupContext: you cannot use context without a provider'
      );

      return {} as CustomersSubscriptionPopupContextModel;
    }

    return value;
  };
